import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  X, Link2, Copy, Check, Clock, Trash2, AlertCircle,
  MessageCircle, Mail, Share2, RefreshCw,
} from "lucide-react";
import api from "../utils/api";

const BASE_URL = window.location.origin;

const EXPIRY_OPTIONS = [
  { value: "1h",    label: "1 Hour"    },
  { value: "24h",   label: "24 Hours"  },
  { value: "7d",    label: "7 Days"    },
  { value: "never", label: "No Expiry" },
];

function fmtExpiry(expiresAt) {
  if (!expiresAt) return "Never expires";
  const d   = new Date(expiresAt);
  const now = new Date();
  if (d < now) return "Expired";
  const diffMs = d - now;
  const diffH  = Math.floor(diffMs / 3600000);
  const diffD  = Math.floor(diffMs / 86400000);
  if (diffH < 1)  return "Expires in < 1h";
  if (diffH < 24) return `Expires in ${diffH}h`;
  return `Expires in ${diffD}d`;
}

function isExpired(share) {
  if (share.revoked) return true;
  if (!share.expires_at) return false;
  return new Date(share.expires_at) < new Date();
}

function getShareUrl(rawToken) {
  return `${BASE_URL}/trip/${rawToken}`;
}

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      onClick={copy}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        background: copied ? "#F0FDF4" : "#F5F5F5",
        border: `1px solid ${copied ? "#BBF7D0" : "#E5E7EB"}`,
        borderRadius: 7, padding: "5px 10px",
        fontSize: 12, fontWeight: 600,
        color: copied ? "#16A34A" : "#374151",
        cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied!" : label}
    </button>
  );
}

function ShareRow({ share, onRevoke, revoking }) {
  const expired  = isExpired(share);
  const statusBg     = expired ? "#F9FAFB" : "#F0FDF4";
  const statusColor  = expired ? "#9CA3AF" : "#16A34A";
  const statusBorder = expired ? "#E5E7EB" : "#BBF7D0";
  const statusLabel  = share.revoked ? "Revoked" : expired ? "Expired" : "Active";

  return (
    <div style={{
      background: "#fff", border: "1px solid #F0F1F3", borderRadius: 10,
      padding: "12px 14px", opacity: expired ? 0.65 : 1,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 4,
          background: statusBg, border: `1px solid ${statusBorder}`,
          borderRadius: 20, padding: "2px 8px",
          fontSize: 11, fontWeight: 600, color: statusColor,
        }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: statusColor }} />
          {statusLabel}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#9CA3AF" }}>
          <Clock size={10} />
          {fmtExpiry(share.expires_at)}
        </div>
        {share.access_count > 0 && (
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>
            {share.access_count} view{share.access_count !== 1 ? "s" : ""}
          </div>
        )}
        {!expired && (
          <button
            onClick={() => onRevoke(share.id)}
            disabled={revoking === share.id}
            title="Revoke link"
            style={{
              marginLeft: "auto",
              background: "none", border: "1px solid #E5E7EB", borderRadius: 7,
              padding: "5px 8px", display: "flex", alignItems: "center",
              color: "#9CA3AF", cursor: revoking === share.id ? "not-allowed" : "pointer",
            }}
          >
            {revoking === share.id
              ? <RefreshCw size={12} style={{ animation: "spin 0.7s linear infinite" }} />
              : <Trash2 size={12} />}
          </button>
        )}
      </div>
    </div>
  );
}

export default function TripShareModal({ trip, onClose }) {
  const [shares,    setShares]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [creating,  setCreating]  = useState(false);
  const [revoking,  setRevoking]  = useState(null);
  const [expiresIn, setExpiresIn] = useState("7d");
  const [newShare,  setNewShare]  = useState(null);
  const [error,     setError]     = useState(null);

  const loadShares = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/trip-shares?tripId=${trip.id}`);
      setShares(data);
    } catch {
      setError("Failed to load shares.");
    } finally {
      setLoading(false);
    }
  }, [trip.id]);

  useEffect(() => { loadShares(); }, [loadShares]);

  async function handleCreate() {
    setCreating(true);
    setError(null);
    setNewShare(null);
    try {
      const { data } = await api.post("/api/trip-shares", { tripId: trip.id, expiresIn });
      setNewShare(data);
      await loadShares();
    } catch (err) {
      setError(err.message || "Failed to create share link.");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(shareId) {
    setRevoking(shareId);
    try {
      await api.delete(`/api/trip-shares/${shareId}`);
      setShares(prev => prev.map(s => s.id === shareId ? { ...s, revoked: true } : s));
    } catch {
      setError("Failed to revoke share.");
    } finally {
      setRevoking(null);
    }
  }

  const activeShares = shares.filter(s => !isExpired(s));

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, zIndex: 900,
        background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.94, y: 20, opacity: 0 }}
        animate={{ scale: 1,    y: 0,  opacity: 1 }}
        exit={{    scale: 0.94, y: 10, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%", maxWidth: 460,
          background: "#fff", borderRadius: 18,
          boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
          border: "1px solid #EBEBEB",
          overflow: "hidden", maxHeight: "92vh",
          display: "flex", flexDirection: "column",
        }}
      >
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #F0F1F3", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "#EEF2FF", border: "1px solid #C7D2FE",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Share2 size={14} color="#4F46E5" />
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Share Trip Status</div>
              </div>
              <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
                {trip.label || `${trip.source?.label || "Source"} → ${trip.destination?.label || "Destination"}`}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, display: "flex", borderRadius: 6 }}
            ><X size={18} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
          {error && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#DC2626",
            }}>
              <AlertCircle size={14} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}

          {newShare && (
            <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#15803D", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <Check size={14} /> Link created successfully!
              </div>
              <div style={{
                background: "#fff", border: "1px solid #D1FAE5", borderRadius: 7,
                padding: "8px 10px", fontSize: 12, color: "#374151", fontFamily: "monospace",
                wordBreak: "break-all", marginBottom: 8, lineHeight: 1.5,
              }}>
                {getShareUrl(newShare.token)}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <CopyButton text={getShareUrl(newShare.token)} label="Copy Link" />
                <button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Track your shipment:\n${getShareUrl(newShare.token)}`)}`, "_blank", "noopener,noreferrer")}
                  style={{ display: "flex", alignItems: "center", gap: 5, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 600, color: "#15803D", cursor: "pointer" }}
                ><MessageCircle size={11} /> WhatsApp</button>
                <button
                  onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent("Shipment Tracking Link")}&body=${encodeURIComponent(`Track your shipment:\n\n${getShareUrl(newShare.token)}`)}`; }}
                  style={{ display: "flex", alignItems: "center", gap: 5, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 600, color: "#2563EB", cursor: "pointer" }}
                ><Mail size={11} /> Email</button>
              </div>
              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 8 }}>
                Shows shipment status only — no live location. Read-only for the recipient.
              </div>
            </div>
          )}

          <div style={{ background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 10, padding: "14px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 12 }}>Generate New Share Link</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Link expires after</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {EXPIRY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setExpiresIn(opt.value)}
                    style={{
                      padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      background: expiresIn === opt.value ? "#4F46E5" : "#fff",
                      color:      expiresIn === opt.value ? "#fff"    : "#374151",
                      border: expiresIn === opt.value ? "1.5px solid #4F46E5" : "1.5px solid #E5E7EB",
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            </div>
            <button
              onClick={handleCreate}
              disabled={creating}
              style={{
                width: "100%", height: 40, borderRadius: 9,
                background: creating ? "#6B7280" : "#4F46E5",
                border: "none", color: "#fff", fontSize: 13, fontWeight: 700,
                cursor: creating ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              }}
            >
              {creating ? <><RefreshCw size={13} style={{ animation: "spin 0.7s linear infinite" }} /> Generating…</> : <><Link2 size={13} /> Generate Link</>}
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 20, color: "#9CA3AF", fontSize: 13 }}>Loading…</div>
          ) : activeShares.length > 0 ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                Active Links ({activeShares.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {activeShares.map(s => (
                  <ShareRow key={s.id} share={s} onRevoke={handleRevoke} revoking={revoking} />
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8, lineHeight: 1.5 }}>
                Active links cannot be re-displayed for security. Copy immediately after creation.
              </div>
            </div>
          ) : !newShare ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#9CA3AF", fontSize: 13 }}>
              No active share links. Generate one above.
            </div>
          ) : null}
        </div>
      </motion.div>
    </motion.div>
  );
}
